import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2, Calendar } from 'lucide-react';
import { fetchSubcounties } from '../services/subcountyService';
import { fetchUniqueStreams } from '../services/streamService';
import { fetchInvoiceReportData } from '../services/revenueService';
import { generateInvoicePDF } from '../utils/invoicePdfUtils';
import { getFinancialYearData } from '../utils/dateHelper'; // Utility for FY formatting
import { useAuth } from '../context/useAuth';

const RevStreamInvoiceData = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    
    // Data Options State
    const [subcounties, setSubcounties] = useState([]);
    const [streams, setStreams] = useState([]);
    const [fyOptions, setFyOptions] = useState({ current: '', previous: '', next: '' });
    

    // Filter State
    const [filters, setFilters] = useState({
        revStream: '',
        financialYear: '',
        subCountyId: user.sub_county_id || '', // Pre-set for restricted users
        paymentStatus: 'Paid',
        includeStart: true, // First Half
        includeEnd: true    // Second Half
    });

    useEffect(() => {
        // Initialize Financial Year Options
        const fy = getFinancialYearData();
        setFyOptions(fy);
        
        // Load initial dependencies
        Promise.all([
            fetchSubcounties(),
            fetchUniqueStreams()
        ]).then(([scData, streamData]) => {
            setSubcounties(scData);
            const invoiceConfdStreams = ["SBP", "Liquor", "Build Plan Approval", "Land Rates"]; // Restrict to those streams for invoice generation
             const filteredStreams = streamData.filter(streamData => invoiceConfdStreams.includes(streamData));
            setStreams(filteredStreams);
            
            // Set defaults after data is loaded
            setFilters(prev => ({
                ...prev,
                financialYear: fy.current,
                revStream: filteredStreams[1] || ''
            }));
        });
    }, []);
 const subCountyName = filters.subCountyId ? subcounties.find(sc => String(sc.id) === String(filters.subCountyId))?.name || 'Selected Sub-County'
    : 'Entire County';
    const halfLabel = !filters.includeStart ? 'Jan - Jun (H2)' : !filters.includeEnd ? 'Jul - Dec (H1)' : 'Full Year';
  const handleDownload = async () => {
        setLoading(true);

        try {
            const yearMatches = filters.financialYear.match(/\d{4}/g);

            const payload = {
                ...filters,
                startYear: parseInt(yearMatches[0]),
                endYear: parseInt(yearMatches[1])
            };
            
            const response = await fetchInvoiceReportData(payload);
            console.log("Totals from API:", response.totals);

            const rawData = response.tableData;
            const totals = response.totals; // ✅ FIXED HERE

            if (!rawData || rawData.length === 0) {
                alert("No records found.");
                return;
            }

            const headers = Object.keys(rawData[0]);
            const dataRows = rawData.map(obj =>
                headers.map(key => obj[key])
            );

            const formattedTableData = [headers, ...dataRows];

            let totalConfig = null;

            if (totals) {

                const totAmountIdx = headers.indexOf('Tot. Amount');
                const paidAmountIdx = headers.indexOf('Paid Amount');
                const balanceIdx = headers.indexOf('Balance');
                const landRentIdx = headers.indexOf('L.Rent');
                const LandRateIdx = headers.indexOf('L.Rate');
                const LandPenaltyIdx = headers.indexOf('Penalty');
                const PHIdx = headers.indexOf('PH FIF Fee');

                const columnIndices = [];
                const values = [];

                if (totAmountIdx !== -1) {
                    columnIndices.push(totAmountIdx);
                    values.push(totals['Tot. Amount'] || '0.00');
                }

                if (paidAmountIdx !== -1) {
                    columnIndices.push(paidAmountIdx);
                    values.push(totals['Paid Amount'] || '0.00');
                }

                if (balanceIdx !== -1) {
                    columnIndices.push(balanceIdx);
                    values.push(totals['Balance'] || '0.00');
                }

                if (PHIdx !== -1) {
                    columnIndices.push(PHIdx);
                    values.push(totals['PH FIF Fee'] || '0.00');
                }
                if (landRentIdx !== -1) {
                    columnIndices.push(landRentIdx);
                    values.push(totals['L.Rent'] || '0.00');
                }
                if (LandRateIdx !== -1) {
                    columnIndices.push(LandRateIdx);
                    values.push(totals['L.Rate'] || '0.00');
                }
                if (LandPenaltyIdx !== -1) {
                    columnIndices.push(LandPenaltyIdx);
                    values.push(totals['Penalty'] || '0.00');
                }


                totalConfig = { columnIndices, values };
            }

            generateInvoicePDF(
                formattedTableData,
                filters,
                user,
                subCountyName,
                halfLabel,
                totalConfig
            );

        } catch (error) {
            console.error("Download Error:", error);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6 border-b pb-4">
                <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-blue-100 shadow-lg">
                    <FileText size={20}/>
                </div>
                <div>
                    <h2 className="text-lg font-black text-slate-800 leading-tight">Revenue Stream Invoice Generator</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Export Official PDF Documentation</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Configuration Column */}
                <div className="space-y-5">
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase mb-1.5 ml-1">Select Revenue Stream</label>
                        <select 
                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700"
                            value={filters.revStream}
                            onChange={(e) => setFilters({...filters, revStream: e.target.value})}
                            disabled={user.isRestricted}
                        >
                            {streams.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1.5 ml-1">Payment Status</label>
                            <select 
                                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700"
                                value={filters.paymentStatus}
                                onChange={(e) => setFilters({...filters, paymentStatus: e.target.value})}
                            >
                                <option value="Paid">Paid</option>
                                <option value="Pending">Pending</option>
                                <option value="Partial">Partial</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1.5 ml-1">sub-county</label>
                            <select 
                                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700"
                                value={filters.subCountyId}
                                onChange={(e) => setFilters({...filters, subCountyId: e.target.value})}
                            >
                                <option value="">Entire County</option>
                                {subcounties.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="text-[11px] font-bold text-slate-500 italic">
                        * Note: Ledger/Invoice generation is currently limited to SBP, Liquor, and Building Plan Approval revenue streams.
                    </div>
                </div>

                {/* Period & Half Selection */}
                <div className="bg-slate-50/50 p-5 rounded-2xl border border-dashed border-slate-200">
                    <div className="flex items-center gap-2 mb-4 text-blue-600">
                        <Calendar size={16} />
                        <span className="text-[10px] font-black uppercase">Coverage: </span>
                        <span className="text-[10px] font-black">(Default: Current FY, 1st & 2nd Half)</span>
                    </div>

                    <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Financial Year</label>
                    <select 
                        className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 mb-5"
                        value={filters.financialYear}
                        onChange={(e) => setFilters({...filters, financialYear: e.target.value})}
                    >
                        <option value={fyOptions.previous}>{fyOptions.previous}</option>
                        <option value={fyOptions.current}>{fyOptions.current}</option>
                        <option value={fyOptions.next}>{fyOptions.next}</option>
                    </select>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                        <PeriodToggle 
                            label="H1 - (Jul to Dec)" 
                            active={filters.includeStart} 
                            onClick={() => setFilters({...filters, includeStart: !filters.includeStart})} 
                        />
                        <PeriodToggle 
                            label="H2 - (Jan to Jun)" 
                            active={filters.includeEnd} 
                            onClick={() => setFilters({...filters, includeEnd: !filters.includeEnd})} 
                        />
                    </div>

                    <button 
                        onClick={handleDownload}
                        disabled={loading || (!filters.includeStart && !filters.includeEnd)}
                        className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white font-black text-xs py-3.5 rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-slate-200"
                    >
                        {loading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16}/>}
                        {loading ? 'EXECUTING REPORT...' : `DOWNLOAD ${filters.revStream} - ${filters.paymentStatus} INVOICE DATA`}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper Component for the Period Checkboxes
const PeriodToggle = ({ label, active, onClick }) => (
    <button 
        onClick={onClick}
        className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${
            active 
            ? 'bg-blue-600 border-blue-600 text-white shadow-md' 
            : 'bg-white border-slate-200 text-slate-400'
        }`}
    >
        <span className="text-[10px] font-black uppercase">{label}</span>
        <div className={`w-3 h-3 rounded flex items-center justify-center ${active ? 'bg-white/20' : 'bg-slate-100'}`}>
            {active && <div className="w-1.5 h-1.5 bg-white rounded-sm" />}
        </div>
    </button>
);

export default RevStreamInvoiceData;